import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import TextField from '@material-ui/core/TextField';
import IconButton from '@material-ui/core/IconButton';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Checkbox from '@material-ui/core/Checkbox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export default function AutocompleteWrapper({
  fieldKey,
  data,
  placeholder,
  onChange,
  defaultValue,
  style,
  isMultiple = false,
  fullWidth = false,
  limitTags = -1,
  disabled = false,
  isCheckbox = false,
  maxOptionToLoadAtTime = 15,
  size = 'medium',
}) {
  const useStyles = makeStyles(() => ({
    inputRoot: {
      paddingRight: 0,
      overflow: 'hidden',
      '& div': {
        maxHeight: style ? style.maxHeight : null,
      },
    },
  }));

  const classes = useStyles();

  const [isScrollBottom, setIsScrollBottom] = useState(false);
  const [filteredListState, setFilteredListState] = useState({ index: 1, filteredList: null, listIsOpen: false });

  const loadedAllData = () => data.length <= maxOptionToLoadAtTime * filteredListState.index;

  const increaseListSize = () => {
    setFilteredListState({
      ...filteredListState,
      index: filteredListState.index + 1,
      filteredList: data.slice(0, maxOptionToLoadAtTime * (filteredListState.index + 1)),
    });
  };

  useEffect(() => {
    if (isScrollBottom) {
      increaseListSize();
    }
  }, [isScrollBottom]);

  function handleChange(event, values) {
    onChange(fieldKey, values);
  }

  const nearBottom = (target = {}) => {
    return target.scrollHeight - target.scrollTop <= target.clientHeight + 50;
  };

  const isDisabled = !filteredListState.listIsOpen || loadedAllData();

  return (
    <Autocomplete
      className={classes.inputRoot}
      style={style}
      id={fieldKey}
      options={data.slice(0, maxOptionToLoadAtTime)}
      getOptionLabel={option => option.name}
      onChange={handleChange}
      limitTags={limitTags}
      multiple={isMultiple}
      value={defaultValue}
      disabled={disabled}
      forcePopupIcon={false}
      size={size}
      onClose={() =>
        setFilteredListState({
          ...filteredListState,
          index: 1,
          filteredList: null,
          listIsOpen: false,
        })
      }
      onOpen={() => {
        setFilteredListState({
          ...filteredListState,
          listIsOpen: true,
        });
      }}
      ListboxProps={{
        onScroll: ({ target }) => setIsScrollBottom(nearBottom(target)),
      }}
      filterOptions={(options, { inputValue }) => {
        if (inputValue === '' || inputValue.length < 3) return filteredListState.filteredList || options;
        return data.filter(x => x.name.toLowerCase().startsWith(inputValue.toLowerCase()));
      }}
      getOptionSelected={(option, value) => option.id === value.id}
      disableCloseOnSelect={isMultiple}
      renderOption={(option, { selected }) => (
        <React.Fragment>
          {isCheckbox && (
            <Checkbox
              color="primary"
              icon={icon}
              checkedIcon={checkedIcon}
              className="autocomplete-checkbox"
              checked={selected}
            />
          )}
          {option.name}
        </React.Fragment>
      )}
      renderInput={params => (
        <TextField
          {...params}
          fullWidth={fullWidth}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            className: classes.inputRoot,
            endAdornment: isDisabled ? (
              <IconButton label="Load more" style={{ paddingRight: '0' }} onClick={increaseListSize} disabled>
                <ArrowDropDownIcon />
              </IconButton>
            ) : (
              <Tooltip title="Load more">
                <IconButton label="Load more" style={{ paddingRight: '0' }} onClick={increaseListSize}>
                  <ArrowDropDownIcon />
                </IconButton>
              </Tooltip>
            ),
          }}
        />
      )}
    />
  );
}
